/**
 * Scheduler filter option sets. Each value carries a visual cue so the
 * dropdowns (and their triggers) read at a glance: brand dots for providers
 * (the ProviderBadge colours: Voluum pink, Binom emerald, Tres Commas amber),
 * semantic dots for statuses, flags for countries, colored chips for tags, and
 * a muted category icon on each "All …" option.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ReactNode } from 'react';
import { Activity, Globe, Layers, SlidersHorizontal, Tags } from 'lucide-react';
import { trackerLabel, type TrackerId } from '@trafficpulse/shared';

import type { SearchableSelectOption } from '@/components/ui';
import {
  tagSelectOptions,
  type TagLike,
} from '@/features/flow-optimizer/components/tag-select-options';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { distinctRowCountries } from '@/features/optimizer-center/utils/row-status';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';
import { cn } from '@/lib/utils';

type Opt = SearchableSelectOption<string>;

const ICON = 'h-3.5 w-3.5 shrink-0 text-muted-foreground';

function glyphRow(label: string, glyph: ReactNode): ReactNode {
  return (
    <span className="inline-flex items-center gap-2">
      {glyph}
      {label}
    </span>
  );
}

function dot(className: string): ReactNode {
  return (
    <span className={cn('h-2 w-2 shrink-0 rounded-full', className)} aria-hidden />
  );
}

function opt(value: string, label: string, glyph: ReactNode): Opt {
  return { value, label, optionContent: glyphRow(label, glyph) };
}

const PROVIDER_DOT: Readonly<Record<TrackerId, string>> = {
  voluum: 'bg-pink-500',
  binom: 'bg-emerald-500',
  'tres-commas': 'bg-amber-500',
};

/** "All providers" + one option per Flow Optimizer tracker, in tracker order. */
export function buildProviderOptions(trackers: readonly TrackerId[]): Opt[] {
  return [
    opt('all', 'All providers', <Layers className={ICON} />),
    ...trackers.map((id) => opt(id, trackerLabel(id), dot(PROVIDER_DOT[id]))),
  ];
}

export const MODE_OPTIONS: Opt[] = [
  opt('all', 'All modes', <SlidersHorizontal className={ICON} />),
  opt('CUSTOM', 'TrafficPulse', dot('bg-primary')),
  opt('STANDARD', 'Standard', dot('bg-muted-foreground')),
];

export const STATUS_OPTIONS: Opt[] = [
  opt('all', 'All statuses', <Activity className={ICON} />),
  opt('auto', 'Auto', dot('bg-success')),
  opt('manual', 'Manual', dot('bg-muted-foreground')),
  opt('paused', 'Paused', dot('bg-warning')),
  opt('error', 'Error', dot('bg-destructive')),
];

export function buildCountryOptions(rows: OptimizerRow[]): Opt[] {
  return [
    opt('all', 'All countries', <Globe className={ICON} />),
    ...distinctRowCountries(rows).map((c) => ({
      value: c.iso2,
      label: c.name,
      optionContent: glyphRow(
        c.name,
        <span aria-hidden>{countryToFlagEmoji(c.iso2)}</span>,
      ),
    })),
  ];
}

export function buildTagOptions(tags: ReadonlyArray<TagLike>): Opt[] {
  return tagSelectOptions(tags, [
    opt('all', 'All tags', <Tags className={ICON} />),
    opt('none', 'Untagged', dot('border border-muted-foreground/60 bg-transparent')),
  ]);
}
